import React, { createRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Checkbox,
  ErrorText,
  Flex,
  Heading,
  Input,
  Label,
  Link,
  PasswordValidator,
} from '@visibly/vui';

import ApiService from '../../../shared/ApiService';

function AccountForm(props) {
  const { partner, fields } = props;

  const { t } = useTranslation();
  const formRef = createRef();
  const [[csrfName, csrfValue]] = Object.entries(ApiService.token());
  const [isValid, setIsValid] = useState(false);
  const [showFullNameValidationMessages, setShowFullNameValidationMessages] = useState(false);
  const [showEmailValidationMessages, setShowEmailValidationMessages] = useState(false);
  const [showPasswordValidationMessages, setShowPasswordValidationMessages] = useState(false);
  const [
    showAgreeCheckboxValidationMessages,
    setShowAgreeCheckboxValidationMessages,
  ] = useState(false);
  const [agreeToPolicies, setAgreeToPolicies] = useState(false);

  const [user, setUser] = useState({
    ...props.user,
    fullName: '',
    email: '',
    password: '',
  });

  const handleInputChange = ({ target }) => {
    setUser({ ...user, [target.id]: target.value });
    if (target.id === 'fullName' && showFullNameValidationMessages) {
      setShowFullNameValidationMessages(false);
    }
    if (target.id === 'email' && showEmailValidationMessages) {
      setShowEmailValidationMessages(false);
    }
    if (target.id === 'password' && showPasswordValidationMessages) {
      setShowPasswordValidationMessages(false);
    }
  };

  const submitHandler = (e) => {
    if (!isValid) {
      e.preventDefault();

      const fullNameRegex = new RegExp(fields.fullName.pattern);
      const fullNameValidationResult = fullNameRegex.test(user.fullName);

      const emailRegex = new RegExp(fields.email.pattern);
      const emailValidationResult = emailRegex.test(user.email);

      const passwordRegex = new RegExp(fields.password.pattern);
      const passwordMaxLength = fields.password.maxLength;
      const passwordValidationResult = (
        passwordRegex.test(user.password) && user.password.length <= passwordMaxLength
      );

      setShowFullNameValidationMessages(!fullNameValidationResult);
      setShowEmailValidationMessages(!emailValidationResult);
      setShowPasswordValidationMessages(!passwordValidationResult);

      setShowAgreeCheckboxValidationMessages(!agreeToPolicies);
    }
  };

  useEffect(() => {
    setIsValid(formRef.current.checkValidity());
  }, [user, agreeToPolicies]);

  return (
    <form
      action={partner.urls.signup}
      method="post"
      ref={formRef}
      noValidate
      onSubmit={submitHandler}
    >
      <Box marginBottom='$xxl'>
        <Heading variant='h5' textAlign='center'>{t('accountForm.heading')}</Heading>
      </Box>

      <input type="hidden" name={csrfName} value={csrfValue} />
      <input type="hidden" name="user[birthday]" value={user.dateOfBirth} className="phi" />
      <input type="hidden" name="user[region_id]" value={user.regionId} className="phi" />

      <Box marginBottom='$lg'>
        <Flex variant='column'>
          <Label
            htmlFor='fullName'
            error={showFullNameValidationMessages && user.fullName === ''}
          >
            {t('accountForm.fields.fullName.label')}
          </Label>
          <Input
            {...fields.fullName}
            id="fullName"
            type="text"
            name="user[full_name]"
            isPhi
            value={user.fullName}
            required
            onChange={handleInputChange}
            error={showFullNameValidationMessages && user.fullName === ''}
          />
          {showFullNameValidationMessages && <ErrorText message={t('accountForm.fields.fullName.validationMessage')} />}
        </Flex>
      </Box>

      <Box marginBottom='$lg'>
        <Flex variant='column'>
          <Label
            htmlFor='email'
            error={showEmailValidationMessages}
          >
            {t('accountForm.fields.email.label')}
          </Label>
          <Input
            {...fields.email}
            id="email"
            type="email"
            name="user[email]"
            isPhi
            value={user.email}
            required
            onChange={handleInputChange}
            error={showEmailValidationMessages}
          />
          {showEmailValidationMessages && <ErrorText message={t('accountForm.fields.email.validationMessage')} />}
        </Flex>
      </Box>

      <Box marginBottom='$lg'>
        <Flex variant='column'>
          <Label
            htmlFor='password'
            error={showPasswordValidationMessages}
          >
            {t('accountForm.fields.password.label')}
          </Label>
          <Input
            {...fields.password}
            id="password"
            type="password"
            name="user[password]"
            isPhi
            value={user.password}
            required
            onChange={handleInputChange}
            error={showPasswordValidationMessages}
          />
          {showPasswordValidationMessages && <ErrorText message={t('accountForm.fields.password.validationMessage')} />}
          <Flex variant='column' style={{ marginTop: '2rem' }}>
            <PasswordValidator password={user.password} />
          </Flex>
        </Flex>
      </Box>

      <Box marginBottom='$xxl'>
        <Flex variant='row'>
          <Checkbox
            id="agreeToPolicies"
            name="agreeToPolicies"
            checked={agreeToPolicies}
            required
            error={showAgreeCheckboxValidationMessages}
            onCheckedChange={(checkedValue) => {
              setAgreeToPolicies(checkedValue);
              setShowAgreeCheckboxValidationMessages(false);
            }}
            label={
              <Trans i18nKey="accountForm.fields.agreeToPolicies.label">
                0
                <Link
                  href={partner.urls.termsOfUse}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  1
                </Link>
                2
                <Link
                  href={partner.urls.privacyPractices}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  3
                </Link>
                4
                <Link
                  href={partner.urls.privacyPolicy}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  5
                </Link>
                6
                <Link
                  href={partner.urls.serviceAgreement}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  7
                </Link>
                8
              </Trans>
            }
          />
          {showAgreeCheckboxValidationMessages && <ErrorText message={t('accountForm.fields.agreeToPolicies.validationMessage')} />}
        </Flex>
      </Box>

      <Button type="submit">
        {t('accountForm.callToAction')}
      </Button>
    </form>
  );
}

AccountForm.propTypes = {
  partner: PropTypes.shape({
    urls: PropTypes.shape({
      signup: PropTypes.string.isRequired,
      termsOfUse: PropTypes.string.isRequired,
      privacyPractices: PropTypes.string.isRequired,
      privacyPolicy: PropTypes.string.isRequired,
      serviceAgreement: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,

  user: PropTypes.shape({
    dateOfBirth: PropTypes.string.isRequired,
    regionId: PropTypes.string.isRequired,
  }).isRequired,

  fields: PropTypes.exact({
    fullName: PropTypes.exact({
      pattern: PropTypes.string.isRequired,
    }),
    email: PropTypes.exact({
      pattern: PropTypes.string.isRequired,
    }),
    password: PropTypes.exact({
      pattern: PropTypes.string.isRequired,
      maxLength: PropTypes.number.isRequired,
    }),
  }),
};

AccountForm.defaultProps = {
  fields: {},
};

export default AccountForm;
